/**
 * Archived Months List Component
 *
 * Displays a table of archived months with their details.
 */

import { Archive, CheckCircle, XCircle, FileArchive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { ArchivedMonth } from '@/types/archive-management';

interface ArchivedMonthsListProps {
  archives: ArchivedMonth[];
  isLoading?: boolean;
}

/**
 * Format month/year as readable string
 */
function formatMonthYear(year: number, month: number): string {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Format file size in human readable format
 */
function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent > 0 ? 1 : 0)} ${units[exponent]}`;
}

/**
 * Format date string to readable format
 */
function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Archived Months List
 * Shows table of all archived months with status and details
 */
export function ArchivedMonthsList({ archives, isLoading }: ArchivedMonthsListProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5" />
            Archived Months
          </CardTitle>
          <CardDescription>Loading archive data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (archives.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5" />
            Archived Months
          </CardTitle>
          <CardDescription>View all archived audit log months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Archive className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No archives yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Archived months will appear here after archival
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileArchive className="h-5 w-5" />
          Archived Months
        </CardTitle>
        <CardDescription>
          {archives.length} month{archives.length !== 1 ? 's' : ''} archived to S3
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>S3 Key</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Last Modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {archives.map(archive => (
              <TableRow key={archive.key}>
                <TableCell className="font-medium">
                  {formatMonthYear(archive.year, archive.month)}
                </TableCell>
                <TableCell>
                  {archive.exists ? (
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Archived
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <XCircle className="h-3 w-3 mr-1" />
                      Missing
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">{archive.key}</code>
                </TableCell>
                <TableCell>{formatFileSize(archive.size)}</TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(archive.lastModified)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
